import type { DecompositionNode } from 'api/data/projects/types';
import { normalizeId } from 'utils/ids';

import type { RichTreeItem } from '../types';
import { toTreeItem } from './toTreeItem';

type PaginatedNodes = { items: DecompositionNode[] }[] | undefined;

function flatMapPaginatedNodes(pages: PaginatedNodes): DecompositionNode[] {
  return pages?.flatMap((page) => page.items) ?? [];
}

/**
 * Sticky decides the ORDER, the server decides the DATA: when a key is in both lists, the server
 * item is emitted at the sticky item's position.
 *
 * Sticky pins are client-owned and never reconciled — the cache is `enabled: false` with
 * `staleTime`/`gcTime: Infinity` (useStickyTopNodes.ts) and survives every refetch — so letting a
 * pinned item shadow the server copy would freeze that node's fields for the whole session. The
 * sticky item is therefore only rendered for keys the server has not returned yet.
 */
export function mergeStickyFirst<T>(stickyItems: T[], serverItems: T[], getKey: (item: T) => string): T[] {
  const serverByKey = new Map<string, T>();
  for (const item of serverItems) {
    const key = getKey(item);
    if (!serverByKey.has(key)) serverByKey.set(key, item);
  }

  const seen = new Set<string>();
  const result: T[] = [];

  for (const item of stickyItems) {
    const key = getKey(item);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(serverByKey.get(key) ?? item);
  }

  for (const item of serverItems) {
    const key = getKey(item);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(item);
  }

  return result;
}

/** Merges sticky-top nodes with paginated server pages (roots or children cache). */
export function mergeStickyWithPaginated(
  stickyNodes: DecompositionNode[],
  pages: PaginatedNodes,
  getKey: (node: DecompositionNode) => string = (node) => normalizeId(node.id)
): DecompositionNode[] {
  return mergeStickyFirst(stickyNodes, flatMapPaginatedNodes(pages), getKey);
}

/** Same as `mergeStickyWithPaginated`, but maps both sides to `RichTreeItem` first. */
export function mergeStickyWithPaginatedAsTreeItems(
  stickyNodes: DecompositionNode[],
  pages: PaginatedNodes,
  parentTreeId: string,
  titlePlaceholder?: string
): RichTreeItem[] {
  const toItem = (node: DecompositionNode) => toTreeItem(node, parentTreeId, titlePlaceholder);
  return mergeStickyFirst(stickyNodes.map(toItem), flatMapPaginatedNodes(pages).map(toItem), (item) =>
    normalizeId(item.dataNodeId)
  );
}
